<template>
  <div class="page stack">
    <!-- 1. How many? -->
    <section v-if="phase === 'count'" class="stack">
      <p class="eyebrow">The week of {{ dayLabel(week.days[0]) }} · order by {{ dayLabel(week.orderBy) }}</p>
      <h1>How many lessons this week?</h1>
      <p class="muted">{{ remaining.length }} left in the course. Pick a number and the next ones fill in; you can move the days before anything is saved.</p>
      <div class="row count-row">
        <button v-for="n in countOptions" :key="n" type="button" class="btn btn-lg count-btn" :class="{ 'btn-primary': n === count }" @click="pickCount(n)">{{ n }}</button>
      </div>
      <router-link to="/" class="btn">Not this week</router-link>
    </section>

    <!-- 2. Which nights? -->
    <section v-else-if="phase === 'assign'" class="stack">
      <p class="eyebrow">The week of {{ dayLabel(week.days[0]) }}</p>
      <h1>{{ chosen.length === 1 ? 'One lesson.' : `${chosen.length} lessons.` }} Which nights?</h1>
      <div v-for="lesson in chosen" :key="lesson.id" class="card assign-row">
        <div class="assign-row__text">
          <span class="assign-row__title">{{ lesson.number }}. {{ lesson.title }}</span>
          <span class="muted small">{{ lesson.dish }} · {{ formatMinutes(lesson.minutes) }}<span v-if="lesson.weekend"> · a weekend afternoon</span></span>
        </div>
        <select v-model="assignment[lesson.id]" class="day-select">
          <option v-for="iso in week.days" :key="iso" :value="iso">{{ dayLabel(iso) }}</option>
        </select>
      </div>
      <p v-if="clash" class="clash">Two lessons on the same night. Move one.</p>
      <div class="row">
        <button type="button" class="btn btn-primary btn-lg" :disabled="clash || saving" @click="confirm">{{ saving ? 'Saving…' : 'Plan the week' }}</button>
        <button type="button" class="btn" :disabled="saving" @click="phase = 'count'">Back</button>
      </div>
      <p class="muted small">Each night goes onto Meal Hat’s schedule as its own entry. Nothing joins the hat.</p>
    </section>

    <!-- 3. The week: order, groceries, nights. -->
    <section v-else class="stack">
      <p class="eyebrow">The week of {{ dayLabel(week.days[0]) }}</p>
      <h1>{{ planned.length === 1 ? 'One lesson' : `${planned.length} lessons` }} this week</h1>

      <div class="card">
        <p class="eyebrow">Nights</p>
        <div v-for="{ lesson, iso } in planned" :key="lesson.id" class="night-row">
          <span class="night-row__day">{{ weekdayLabel(iso) }}</span>
          <span class="night-row__text">
            <router-link :to="`/lesson/${lesson.id}`" class="night-row__title">{{ lesson.number }}. {{ lesson.title }}</router-link>
            <span class="muted small">{{ lesson.dish }}</span>
          </span>
          <span v-if="progress[lesson.id]?.cookedAt" class="pill pill--green">cooked</span>
          <router-link v-else :to="`/cook/${lesson.id}`" class="btn btn-sm">Cook</router-link>
        </div>
        <p v-if="entry?.moved?.length" class="moved">
          <strong>Moved on Meal Hat’s schedule:</strong>
          <span v-for="(m, i) in entry.moved" :key="i" class="moved__item">{{ m }}</span>
          <span class="muted small">Adjust in Meal Hat if a new day is wrong.</span>
        </p>
        <button type="button" class="btn btn-sm" @click="replan">Change the plan</button>
      </div>

      <div class="card">
        <p class="eyebrow">From South Mountain Creamery · order by {{ dayLabel(week.orderBy) }}</p>
        <ul class="plain-list">
          <li v-for="item in order" :key="item.creameryId" class="order-row">
            <a :href="itemUrl(item.creameryId, item.name)" target="_blank" rel="noopener" class="order-row__name">{{ item.name }}</a>
            <span class="muted small">
              <template v-for="(line, i) in item.lines" :key="i">{{ i ? ' + ' : '' }}{{ line.qty }} <span class="muted">(lesson {{ line.lessonNumber }})</span></template>
            </span>
            <span v-for="(note, i) in item.notes" :key="`n${i}`" class="muted small order-row__note">{{ note }}</span>
          </li>
        </ul>
        <div class="row">
          <a href="https://store.southmountaincreamery.com/p/shop/addons/meat-seafood" target="_blank" rel="noopener" class="btn">Open the store</a>
          <button type="button" class="btn" :class="{ 'btn-success': entry?.orderedAt }" @click="markOrdered">{{ entry?.orderedAt ? 'Ordered ✓' : 'Mark as ordered' }}</button>
        </div>
      </div>

      <div class="card">
        <p class="eyebrow">Everything else</p>
        <p v-if="kitchenLoading" class="muted">Checking the fridge…</p>
        <ul v-else class="plain-list">
          <li v-for="ing in assessed" :key="ing.name" class="ing-row">
            <span class="ing-row__dot" :class="`ing-row__dot--${ing.status}`"></span>
            <span class="ing-row__text">
              <span>{{ ing.name }}<span v-if="ing.qty" class="muted"> · {{ ing.qty }}</span></span>
              <span class="muted small">
                <template v-if="ing.status === 'have'">In the house<span v-if="ing.fridgeName && ing.fridgeName.toLowerCase() !== ing.name.toLowerCase()"> as “{{ ing.fridgeName }}”</span><span v-if="ing.timers[0]">, {{ daysLeft(ing.timers[0]) }} days left</span></template>
                <template v-else-if="ing.status === 'listed'">Already on the Meal Hat list</template>
                <template v-else-if="ing.status === 'pantry'">Pantry staple; check you have it</template>
                <template v-else>Goes on the Meal Hat list</template>
              </span>
            </span>
          </li>
        </ul>
        <div class="row">
          <a href="/#/shopping-list" class="btn">Open the Meal Hat list</a>
        </div>
        <p class="muted small">These go on Meal Hat’s list by themselves, like any scheduled meal’s; pantry staples are yours to check.</p>
      </div>
      <router-link to="/" class="btn">Back to the course</router-link>
    </section>
  </div>
</template>

<script>
// The week, planned as a unit: how many lessons, which nights, one creamery
// order, and each night written onto Meal Hat's schedule
// as a one-off. See utils/week.js for the cycle and the reasoning.
import { computed, ref, watch } from 'vue';
import { trackEvent } from '../utils/usage.js';
import { useRoute, useRouter } from 'vue-router';
import { LESSONS, formatMinutes } from '../curriculum/lessons.js';
import { planningWeek, defaultAssignment, aggregateOrder, aggregateGrocery, dayLabel, weekdayLabel } from '../utils/week.js';
import { loadProgress, markLesson, loadWeek, saveWeek } from '../utils/course.js';
import { moveOnSchedule, removeFromSchedule, refreshScheduleRows } from '../utils/mealHatSchedule.js';
import { planDisplacements, movePatch, describeMove } from '../utils/displace.js';
import { dbGet, dbPatch } from '../store/db.js';
import { loadKitchen } from '../utils/hat.js';
import { assessIngredients, daysLeft } from '../utils/inHouse.js';
import { loadCatalog, itemUrl } from '../utils/creamery.js';
import { setActivity, setAppError, setWhere } from '../state.js';

export default {
  name: 'WeekScreen',
  props: { hat: { type: String, default: null } },
  setup (props) {
    const route = useRoute();
    const router = useRouter();
    const week = planningWeek();
    const progress = ref({});
    const entry = ref(null);
    const phase = ref('count');
    const count = ref(Number(route.query.count) || 0);
    const assignment = ref({});
    const saving = ref(false);
    const kitchen = ref({ catalog: {}, shoppingList: {}, timers: {} });
    const kitchenLoading = ref(true);
    const catalog = ref({});
    setActivity('week');
    setWhere({ screen: 'week', lessonId: null, phase: null, stepIndex: 0 });

    const remaining = computed(() => LESSONS.filter((l) => !progress.value[l.id]?.cookedAt && !(entry.value?.lessons || {})[l.id]));
    const countOptions = computed(() => Array.from({ length: Math.min(5, remaining.value.length) }, (_, i) => i + 1));
    const chosen = computed(() => remaining.value.slice(0, count.value));
    const clash = computed(() => new Set(Object.values(assignment.value)).size < chosen.value.length);

    const planned = computed(() => Object.entries(entry.value?.lessons || {})
      .map(([id, iso]) => ({ lesson: LESSONS.find((l) => l.id === id), iso }))
      .filter((p) => p.lesson)
      .sort((a, b) => a.iso.localeCompare(b.iso)));
    const plannedLessons = computed(() => planned.value.map((p) => p.lesson));
    const order = computed(() => aggregateOrder(plannedLessons.value));
    const assessed = computed(() => assessIngredients({ grocery: aggregateGrocery(plannedLessons.value) }, kitchen.value));

    async function load () {
      if (!props.hat) return;
      refreshScheduleRows(props.hat);
      const [p, w] = await Promise.all([loadProgress(props.hat), loadWeek(props.hat, week.key)]);
      progress.value = p;
      entry.value = w;
      if (w?.lessons && Object.keys(w.lessons).length) {
        phase.value = 'week';
        kitchenLoading.value = true;
        kitchen.value = await loadKitchen(props.hat);
        kitchenLoading.value = false;
      } else if (count.value) {
        pickCount(count.value);
      }
    }
    watch(() => props.hat, load, { immediate: true });
    loadCatalog().then((items) => { catalog.value = items; return items; }).catch(() => {});

    function pickCount (n) {
      count.value = n;
      assignment.value = defaultAssignment(chosen.value, week.days);
      phase.value = 'assign';
    }

    async function confirm () {
      saving.value = true;
      try {
        // Anything Meal Hat already has on a lesson night slides forward
        // first, so the lesson row never shares a date.
        const dates = chosen.value.map((l) => assignment.value[l.id]);
        const [drawn, meals] = await Promise.all([
          dbGet(`${props.hat}/drawnMeals`, 'orderBy="assignedDate"&startAt="' + week.days[0] + '"').catch(() => null),
          dbGet(`${props.hat}/meals`).catch(() => null)
        ]);
        const moves = planDisplacements(drawn || {}, dates, meals || {});
        for (const move of moves) await dbPatch(props.hat, movePatch(move, meals?.[move.mealId]));

        const lessons = {};
        for (const lesson of chosen.value) {
          const iso = assignment.value[lesson.id];
          lessons[lesson.id] = iso;
          const rowId = await moveOnSchedule(props.hat, lesson, progress.value[lesson.id]?.scheduleRowId, iso);
          await markLesson(props.hat, lesson.id, { plannedFor: iso, scheduleRowId: rowId, week: week.key });
        }
        trackEvent('week-planned');
        await saveWeek(props.hat, week.key, {
          lessons,
          plannedAt: Date.now(),
          moved: moves.length ? moves.map((m) => describeMove(m, dayLabel)) : null
        });
        await load();
      } catch (error) {
        setAppError(`Could not save the plan: ${error.message}`);
      } finally {
        saving.value = false;
      }
    }

    async function replan () {
      // Take the uncooked nights back off Meal Hat's schedule and start over.
      try {
        for (const { lesson } of planned.value) {
          if (progress.value[lesson.id]?.cookedAt) continue;
          await removeFromSchedule(props.hat, progress.value[lesson.id]?.scheduleRowId);
          await markLesson(props.hat, lesson.id, { plannedFor: null, scheduleRowId: null, week: null });
        }
        await saveWeek(props.hat, week.key, { lessons: null, plannedAt: null, moved: null });
        entry.value = null;
        count.value = 0;
        phase.value = 'count';
        await load();
      } catch (error) {
        setAppError(`Could not change the plan: ${error.message}`);
      }
    }

    async function markOrdered () {
      const orderedAt = entry.value?.orderedAt ? null : Date.now();
      if (orderedAt) trackEvent('order-marked');
      await saveWeek(props.hat, week.key, { orderedAt });
      entry.value = { ...entry.value, orderedAt };
    }

    return { week, phase, count, countOptions, remaining, chosen, assignment, clash, saving, pickCount, confirm, replan, planned, progress, entry, order, catalog, assessed, kitchenLoading, markOrdered, formatMinutes, dayLabel, weekdayLabel, itemUrl, daysLeft, router };
  }
};
</script>

<style scoped>
.small { font-size: 0.85rem; }
.count-row { gap: 0.6rem; }
.count-btn { min-width: 3.6rem; font-size: 1.3rem; }
.assign-row { display: flex; align-items: center; gap: 1rem; justify-content: space-between; }
.assign-row__text { display: flex; flex-direction: column; min-width: 0; }
.assign-row__title { font-weight: 700; }
.day-select { font: inherit; padding: 0.5rem 0.6rem; border: 1px solid var(--line); border-radius: 10px; background: var(--surface); color: var(--text); min-height: 44px; }
.clash { color: var(--danger); margin: 0; }
.moved { display: flex; flex-direction: column; gap: 0.2rem; background: var(--accent-soft); border-radius: var(--radius); padding: 0.6rem 0.9rem; margin: 0.25rem 0 0.75rem; }
.night-row { display: flex; align-items: center; gap: 0.9rem; padding: 0.5rem 0; border-bottom: 1px solid var(--line); }
.night-row:last-of-type { border-bottom: 0; margin-bottom: 0.6rem; }
.night-row__day { width: 6.5rem; font-weight: 700; flex: 0 0 auto; }
.night-row__text { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.night-row__title { color: var(--text); font-weight: 600; text-decoration: none; }
.plain-list { list-style: none; margin: 0 0 0.75rem; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
.order-row { display: flex; flex-direction: column; }
.order-row__name { font-weight: 600; }
.order-row__note { font-style: italic; }
.ing-row { display: flex; gap: 0.6rem; align-items: flex-start; }
.ing-row__dot { width: 0.7rem; height: 0.7rem; border-radius: 50%; margin-top: 0.45rem; flex: 0 0 auto; background: var(--accent); }
.ing-row__dot--have { background: var(--green); }
.ing-row__dot--listed { background: #b58a2c; }
.ing-row__dot--pantry { background: #cfc6b8; }
.ing-row__text { display: flex; flex-direction: column; }
</style>
